/**
 * useVocabulary Hook
 * 
 * P1 VOCABULARY MANAGEMENT:
 * Fetches vocabulary options for dropdowns with caching.
 */

import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";

/**
 * Fetch vocabulary entries for a specific type
 * @param {string} type - "channels", "verticals", or "platforms"
 * @param {Object} options - Query options
 * @returns {Object} - { data, isLoading, error, refetch }
 */
export function useVocabulary(type, options = {}) {
  const { selectedTenantId } = useTenant();
  
  return useQuery({
    queryKey: ["vocabulary", type, selectedTenantId],
    queryFn: async () => {
      const res = await api.get(`/admin/vocabulary/${type}`);
      return res.data.entries || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    enabled: !!selectedTenantId,
    ...options,
  });
}

/**
 * Get vocabulary options formatted for Select components
 * @param {string} type - "channels", "verticals", or "platforms"
 * @returns {Object} - { options, isLoading, error }
 */
export function useVocabularyOptions(type) {
  const { data, isLoading, error } = useVocabulary(type);
  
  const options = (data || [])
    .filter(entry => entry.is_active)
    .sort((a, b) => a.sort_order - b.sort_order)
    .map(entry => ({
      value: entry.key,
      label: entry.label,
    }));
  
  return { options, isLoading, error };
}

/**
 * Get channel options
 */
export function useChannelOptions() {
  return useVocabularyOptions("channels");
}

/**
 * Get vertical options
 */
export function useVerticalOptions() {
  return useVocabularyOptions("verticals");
}

/**
 * Get platform options
 */
export function usePlatformOptions() {
  return useVocabularyOptions("platforms");
}
